<template>
    <div class="log">

        <div v-if="log.timeslots.length == 0">
            <card>
                <p>{{ $t('sessions.remedial-session-notice') }}</p>
            </card>
        </div>
        
        <div v-else>
            <card class="filter mb-3">
                <filters :value="filters"
                    :types="types"
                    :lang="lang"
                    :registration="registration"
                    :restore="true"
                    @input="$emit('filtersChanged', $event)"
                >
                </filters>
            </card>

            <div :class="{'pb-2': active, 'pb-4': !active}">
                <base-button type="white mr-1" size="sm" @click="expandAll">
                    <i class="fas fa-chevron-down"></i>
                    {{ $t('common.expand-all') }}
                </base-button>
                
                <base-button type="white" size="sm" @click="collapseAll">
                    <i class="fas fa-chevron-up"></i>
                    {{ $t('common.collapse-all') }}
                </base-button>
            </div>
        </div>

        <div ref="timeslots">
            <collapse class="timeslots pb-3" ref="timeslots-collapse">

                <div v-for="(timeslot, index) in visibleTimeslots" :key="index" :class="{'pt-4': !active}">

                    <features v-if="stats"
                        :registration="registration"
                        :simulation="simulation"
                        :session="session"
                        :stats="stats"
                        :timeslot="timeslot"
                        @refresh="$emit('refresh')"
                        @createNote="$refs['modals'].createNote($event)"
                        @createPicture="$refs['modals'].createPicture($event)"
                        @createIssue="$refs['modals'].createIssue($event)"
                    >
                    </features>

                    <collapse-item-card :name="index"
                        :active-default="!defaultTimeslotCollapsing(timeslot.id)"
                        @change="timeslotCollapsed(timeslot.id, $event)"
                    >
                    
                        <template slot="title">
                            <div class="row align-items-center pl-4">
                                <div class="col" style="flex: 0 0 80px">
                                    <h5 class="mb-0 float-left" style="font-size:1.1rem;font-weight:700">
                                        <!--
                                        <i class="fas fa-clock mr-2" style="font-size:1rem"></i>
                                        -->
                                        {{ timeslot.time }}
                                    </h5>
                                </div>
                                <div class="col pl-0">
                                    <h6 class="font-weight-normal text-sm text-uppercase mb-0">
                                        {{ timeslot.instructor_summary }}
                                    </h6>
                                </div>
                            </div>
                        </template>

                        <template>
                            <el-table class="table-responsive table-flush"
                                :show-header="false"
                                :data="filteredItems(timeslot)"
                                :empty-text="$t('logs.no-filtered-item')"
                                v-if="filteredItems(timeslot).length"
                            >
                                <el-table-column width="60" class-name="type-cell">
                                    <template v-slot="{row}">
                                        <div class="text-center type">

                                            <el-popover placement="top">
                                                <a slot="reference" href="#" @click.prevent="">
                                                    <i :class="types[row.type].icon+' text-'+types[row.type].color"></i>
                                                </a>
                                                <h5 class="text-center mb-0">
                                                    {{ types[row.type].name[lang] }}
                                                </h5>
                                            </el-popover>

                                        </div>
                                    </template>
                                </el-table-column>

                                <el-table-column class-name="content-cell">
                                    <template v-slot="{row}">

                                        <div :class="' pl-3 border-left border-'+types[row.type].color">
                                            <div class="tinymce" v-html="row.description"></div>
                                        </div>

                                        <div class="mb-2" v-if="row.competency_ids && row.competency_ids.length && filters.showCompetencies">
                                            <badge type="primary" class="mr-1"
                                                    v-for="(competency_id, index) in row.competency_ids"
                                                    :key="index">
                                                {{ competencies[competency_id]['name_'+lang] }}
                                            </badge>
                                        </div>

                                    </template>
                                </el-table-column>
                                
                            </el-table>
                        </template>
                    </collapse-item-card>
                </div>
            </collapse>
        </div>
        
        <div class="text-center mb-3" v-if="showTerminate">
            <base-button type="primary" @click="stopSimulation">
                <i class="fas fa-stop-circle mr-1"></i> {{ $t('sessions.terminate-session') }}
            </base-button>
        </div>

        <div class="text-center mb-3 mt--4" v-if="showLoadingMore">
            <loading-more @click.prevent="loadMore"></loading-more>
        </div>

        <div style="padding-top:200px;padding-bottom:200px;">
            <h3 class="text-muted text-center">ASTRAL</h3>
            <!--
                This div is here to solve a Chrome refresh issue at the bottom of the page.
            -->
        </div>

        <portal to="modals">

            <confirm-modal v-model="modals.confirmStop">
                <p>{{ $t('sessions.terminate-session-confirm') }}</p>
            </confirm-modal>

            <modals ref="modals"
                :modals="modals"
                :registration="registration"
                :simulation="simulation"
                :session="session"
                :timeslot="timeslot"
                :competencies="competencies"
                :grid-competencies="gridCompetencies"
                :lang="lang"
                @refresh="$emit('refresh')"
            ></modals>

        </portal>
    </div>
</template>

<script>
    import ModalsController from '@trax/classes/ModalsController'
    import Features from './TimeslotFeatures'
    import Modals from './TimeslotModals'
    import Filters from './Filters'

    export default {

        components: {
            Features, Filters, Modals
        },

        props: {

            log: {
                type: Object,
                required: true
            },

            registration: {
                type: Object,
                required: true
            },

            simulation: {
                type: Object,
                required: true
            },

            session: {
                type: Object,
                default: null
            },
            
            competencies: {
                type: Object,
                default: null
            },
                        
            gridCompetencies: {
                type: Object,
                default: null
            },

            types: {
                type: Object,
                required: true
            },

            stats: {
                type: Object,
                default: null
            },

            lang: {
                type: String,
                default: "en"
            },
            
            redirect: {
                type: Object,
                default: null
            },

            filters: {
                type: Object,
                default: {}
            },
        },

        data() {
            return {
                modals: new ModalsController(['confirmStop',
                    'editNote', 'updateNote',
                    'editPictureFile', 'updatePicture',
                    'confirmDeleteFeedback',
                    'editIssue', 'updateIssue', 'confirmDeleteIssue'
                ]),
                visibleTimeslotsCount: 10,
                defaultCollapsing: false,
                timeslotsCollapsing: null
            }
        },

        created() {
            this.modals.init(this, 'modals')
            this.restoreCollapsing()
        },

        mounted() {
            this.$nextTick(function() {
                window.addEventListener('scroll', this.onScroll);
            });        
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll);
        },

        computed: {

            positions() {
                let positions = [{
                    id: 1,
                    name: this.registration.degree.simulation_role_1_name[this.lang]
                }]
                if (this.registration.degree.simulation_role_2) {
                    positions.push({
                        id: 2,
                        name: this.registration.degree.simulation_role_2_name[this.lang]
                    })
                }
                return positions
            },

            active() {
                return this.$permissions.hasRight('dashboard.pilot-log.access')
                    || (this.session && this.session.started_at && !this.session.closed_at)
            },

            filteredTimeslots() {
                return this.log.timeslots.filter(timeslot => {
                    return this.filteredItems(timeslot).length > 0
                })
            },

            visibleTimeslots() {
                return this.filteredTimeslots.slice(0, this.visibleTimeslotsCount)
            },

            showTerminate() {
                return this.active
                    && this.session
                    && !this.session.ended_at
                    && this.$permissions.hasRight('dashboard.learners.write')
                    && this.visibleTimeslotsCount >= this.filteredTimeslots.length
            },

            showLoadingMore() {
                return this.visibleTimeslotsCount < this.filteredTimeslots.length
            }
        },

        methods: {

            restoreCollapsing() {
                let logCollapse = this.$auth.getUiConfig('log_collapse')
                if (logCollapse && logCollapse.id == this.log.id) {
                    this.timeslotsCollapsing = logCollapse.timeslots
                    this.defaultCollapsing = logCollapse.default
                } else {
                    this.timeslotsCollapsing = {}
                    this.rememberCollapsing()
                }
            },
  
            rememberCollapsing() {
                let collapsing = {
                    id: this.log.id,
                    default: this.defaultCollapsing,
                    timeslots: this.timeslotsCollapsing,
                }
                this.$auth.setUiConfig(collapsing, 'log_collapse')
            },

            defaultTimeslotCollapsing(id) {
                return this.timeslotsCollapsing[id] !== undefined ? this.timeslotsCollapsing[id] : this.defaultCollapsing
            },

            timeslotCollapsed(id, active) {
                this.timeslotsCollapsing[id] = !active
                this.rememberCollapsing()
            },

            expandAll() {
                this.$refs['timeslots-collapse'].expandAll();
                this.defaultCollapsing = false
                this.timeslotsCollapsing = {}
                this.rememberCollapsing()
            },

            collapseAll() {
                this.$refs['timeslots-collapse'].collapseAll();
                this.defaultCollapsing = true
                this.timeslotsCollapsing = {}
                this.rememberCollapsing()
            },

            filteredItems(timeslot) {
                return timeslot.items.filter(item => {

                    const byType = this.filters.selectedTypes.length == 0
                        || this.filters.selectedTypes.includes(item.type)
                        || item.type == 'position_change'   // Position change always visible!

                    const byPosition = (this.filters.position1 && item.simulation_role_1)
                        || (this.filters.position2 && item.simulation_role_2)
                        || item.type == 'position_change'   // Position change always visible!
                    
                    return byType && byPosition
                })
            },

            loadMore() {
                if (this.visibleTimeslotsCount < this.filteredTimeslots.length) {
                    this.visibleTimeslotsCount = this.visibleTimeslotsCount + 10
                }
            },

            onScroll() {
                const timeslotsDiv = this.$refs["timeslots"];
                if (timeslotsDiv) {
                    const timeslotsBottom = timeslotsDiv.getBoundingClientRect().bottom;
                    if (timeslotsBottom < window.innerHeight) {                 
                        this.loadMore()
                    }
                }  
            },

            stopSimulation() {
                this.modals.open('confirmStop').then(confirmResponse => {
                    if (confirmResponse.confirmed) {
                        this.$loader.show()
                        axios.post('/astral/api/front/dashboard/sessions/'+this.session.id+'/stop')
                        .then(resp => {
                            if (this.redirect) {
                                this.$router.push(this.redirect)
                            }
                        })
                        .catch(err => {
                            this.$auth.handleErrorVm(err, this)
                        })        
                    }
                })
            }
        }
    }
</script>

<style scoped>

    .log .timeslots >>> .card {
        margin-top: -20px;
        margin-bottom: 10px;
    }
    .log .timeslots >>> .card-header {
        border-bottom: 0px solid;
    }
    .log .timeslots >>> .card-header:after {
        right: auto;
    }
    .log .timeslots >>> .card-body {
        padding: 0;
    }
    .log .timeslots >>> .card-body tr.el-table__row td {
        padding-top: 0;
        border-bottom: 0px solid;
    }
    .log .timeslots >>> .card-body tr.el-table__row:last-child td {
        padding-bottom: 10px;
    }
    .log .timeslots >>> .type-cell {
        padding-right: 0;
        vertical-align: top;
    }
    .log .timeslots >>> .type-cell .cell i {
        font-size: 1.6rem;
    }
    .log .timeslots >>> .content-cell {
        padding-left: 10px;
    }
    .log .timeslots >>> .content-cell .border-left {
        border-width: 2px !important;
    }

</style>
